import React from 'react';

import { Customer, CustomerAuthLevel } from 'models/Customer';
import { DialogueCardData } from 'models/DialogCard';
import { useAppDispatch, useAppSelector } from 'store/store';

import { useArticles, useCurrentArticle } from '../../hooks/shipment.hooks';
import {
  getAttemptedDeliveryCard,
  getAwaitingCollectionCard,
  getDeliveryOptionsCard,
  getFailedRedirectionCard,
  getInternationalParcelCard,
  getLateEddCard,
  getLatestEventCard,
  getPendingExplainerCard,
  getTimeWindowCard,
} from '../cards';
import { getRedirectCard } from '../cards/Redirect';

export const useDialogueCard = () => {
  const currentArticle = useCurrentArticle();
  const articles = useArticles();
  const isMultiArticleConsignment = articles.length > 1;
  let attemptedDeliveryPhotoAvailable = false;

  const deliveryEstimate = useAppSelector((state) => state.deliveryEstimate);
  const safeDropPreference = useAppSelector((state) => state.deliveryPreference);
  const customerData = useAppSelector((state) => state.customer.data);
  const { knownToken = '', isAuthenticated = false } = (customerData as Customer) || {};

  const dispatch = useAppDispatch();

  const [isRedirectModalOpen, setIsRedirectModalOpen] = React.useState(false);
  const [isLiiaspModalOpen, setIsLiiaspModalOpen] = React.useState(false);

  if (!currentArticle || safeDropPreference.loading) return null;

  let dialogueCard: DialogueCardData | null;

  const internationalTrackingLink = currentArticle.internationalTracking?.href;
  if (internationalTrackingLink) return getInternationalParcelCard(internationalTrackingLink);

  const { serviceCode } = currentArticle;
  dialogueCard = getTimeWindowCard(currentArticle.timeWindow, serviceCode);
  if (dialogueCard) return dialogueCard;

  const {
    articleId,
    events,
    collectionInstruction,
    redirectStatus,
    milestones,
    trackStatusOfArticle,
    previousDelivery,
    accessCode,
    buildingAccessInstruction,
    redirectAddress,
    address,
    isStarTrackPremiumArticle,
  } = currentArticle;
  const latestEvent = events?.[0] || null;

  const {
    atlEligible,
    atlLocation,
    atlStatus,
    userOwnership,
    safeDropIneligibility,
    attemptedDeliveryImageExists,
    isMatchedToApcn,
  } = currentArticle;

  let authLevel = CustomerAuthLevel.ANONYMOUS;
  attemptedDeliveryPhotoAvailable = isMatchedToApcn && attemptedDeliveryImageExists && !isMultiArticleConsignment;

  if (knownToken) {
    authLevel = CustomerAuthLevel.KNOWN;
    attemptedDeliveryPhotoAvailable = attemptedDeliveryImageExists && !isMultiArticleConsignment;
  }

  if (isAuthenticated) {
    authLevel = CustomerAuthLevel.AUTHENTICATED;
    attemptedDeliveryPhotoAvailable = userOwnership && attemptedDeliveryImageExists && !isMultiArticleConsignment;
  }

  dialogueCard = getAwaitingCollectionCard(
    isAuthenticated,
    articleId,
    latestEvent,
    collectionInstruction,
    accessCode,
    attemptedDeliveryPhotoAvailable,
    buildingAccessInstruction,
    redirectStatus,
    serviceCode,
    address
  );
  if (dialogueCard) return dialogueCard;

  dialogueCard = getAttemptedDeliveryCard(latestEvent, attemptedDeliveryPhotoAvailable);
  if (dialogueCard) return dialogueCard;

  dialogueCard = getRedirectCard(redirectStatus, latestEvent, redirectAddress);
  if (dialogueCard && authLevel !== CustomerAuthLevel.ANONYMOUS) return dialogueCard;

  const latestEdd = deliveryEstimate.calculatedEdd?.onDate ?? deliveryEstimate.latestEdd ?? null;
  dialogueCard = getLateEddCard(latestEdd, articleId, isStarTrackPremiumArticle);
  if (dialogueCard) return dialogueCard;

  const latestMilestone = latestEvent?.milestone ?? milestones?.[0].name ?? trackStatusOfArticle ?? 'Pending';
  dialogueCard = getFailedRedirectionCard(redirectStatus, latestMilestone, previousDelivery);
  if (dialogueCard) return dialogueCard;

  dialogueCard = getLatestEventCard(latestEvent?.eventCode || null);
  if (dialogueCard) return dialogueCard;

  const { articleRedirect } = currentArticle;

  dialogueCard = getDeliveryOptionsCard(
    authLevel,
    atlEligible,
    articleRedirect,
    articleId,
    isRedirectModalOpen,
    setIsRedirectModalOpen,
    isLiiaspModalOpen,
    setIsLiiaspModalOpen,
    dispatch,
    latestMilestone,
    atlLocation,
    safeDropPreference.data,
    userOwnership,
    atlStatus,
    safeDropIneligibility?.code,
    serviceCode,
    isStarTrackPremiumArticle
  );
  if (dialogueCard) return dialogueCard;

  dialogueCard = getPendingExplainerCard(latestMilestone);

  return dialogueCard ?? { type: null, analyticsId: 'null' };
};
